/** Arborescence des paquets (ADR 0010), construite à partir de la liste à plat. */
import type { Paquet } from '../../supabase/conversions';

export interface NoeudPaquet {
  paquet: Paquet;
  niveau: number;
  /** Chemin des noms, du paquet principal à ce paquet. */
  chemin: string[];
  enfants: NoeudPaquet[];
  /** Paquet principal qui contient ce paquet (lui-même pour un paquet principal). */
  principalId: string;
}

const comparer = (a: Paquet, b: Paquet) => a.nom.localeCompare(b.nom, 'fr', { numeric: true });

export function construireArbre(paquets: readonly Paquet[]): NoeudPaquet[] {
  const enfants = new Map<string | null, Paquet[]>();
  for (const p of paquets) {
    const liste = enfants.get(p.parentId) ?? [];
    liste.push(p);
    enfants.set(p.parentId, liste);
  }
  const construire = (
    parent: string | null,
    niveau: number,
    chemin: string[],
    principal: string | null,
  ): NoeudPaquet[] =>
    (enfants.get(parent) ?? []).sort(comparer).map((paquet) => {
      const cheminPaquet = [...chemin, paquet.nom];
      const principalId = principal ?? paquet.id;
      return {
        paquet,
        niveau,
        chemin: cheminPaquet,
        principalId,
        enfants: construire(paquet.id, niveau + 1, cheminPaquet, principalId),
      };
    });
  return construire(null, 0, [], null);
}

/** Les nœuds dans l'ordre d'affichage (parcours en profondeur). */
export function aplatir(noeuds: readonly NoeudPaquet[]): NoeudPaquet[] {
  return noeuds.flatMap((n) => [n, ...aplatir(n.enfants)]);
}

/** Index des nœuds par identifiant de paquet. */
export function indexer(noeuds: readonly NoeudPaquet[]): Map<string, NoeudPaquet> {
  return new Map(aplatir(noeuds).map((n) => [n.paquet.id, n]));
}

/** Vrai si `id` est `ancetre` ou l'un de ses descendants. */
export function estDescendant(
  index: ReadonlyMap<string, NoeudPaquet>,
  id: string,
  ancetre: string,
) {
  let courant: string | null | undefined = id;
  while (courant) {
    if (courant === ancetre) return true;
    courant = index.get(courant)?.paquet.parentId;
  }
  return false;
}
